import { v } from "convex/values";
import { defineTable } from "convex/server";
import {
  internalControl,
  defineTableWithInternalControl,
} from "../../lib/shared/validators";
import { customFieldValues } from "../custom_fields";
import { automationCondition } from "./automations";

// ============================================================================
// COLECCIONES PROPIAS (tablas creadas por el usuario)
// ============================================================================
//
// Una colección es una tabla definida por la organización: sus campos son
// definiciones de `custom_fields` con `entityType: "collection"` y su
// `collectionId`; sus registros viven en `collection_records` con los valores
// en `values` (mismo formato que `customFields` de las tablas del sistema).
//
// Permisos:
//   - Crear/editar/borrar colecciones y sus campos: módulo `custom_fields`
//     (Configuración → Colecciones).
//   - Ver/crear/editar/borrar registros: módulo `collection_records` (techo
//     por rol) y, si la colección tiene `allowedRoleIds`, además el rol del
//     usuario debe estar en la lista (los admins siempre pasan). Crear,
//     editar y borrar se pueden acotar más por colección con `recordRoleIds`
//     (acciones predeterminadas del panel "Acciones de fila"). Ver helpers.ts.

/** Tope de líneas por documento (cada hija puede fijar uno menor). */
export const MAX_CHILD_LINES = 500;
/** Hijas declaradas por colección. */
export const MAX_COLLECTION_CHILDREN = 5;

export const collectionChild = v.object({
  /** Colección de las líneas. */
  collectionId: v.id("collections"),
  /** Campo relación de la hija que apunta al padre. */
  parentFieldKey: v.string(),
  /** Tope de líneas por documento (≤ MAX_CHILD_LINES). */
  max: v.optional(v.number()),
  /**
   * Campo de la hija que ordena las líneas. Un número es la posición (1, 2,
   * 3…, la pone el motor); una fecha o un texto ordena por su valor (p. ej.
   * un historial por fecha). Ver `sortLinesByField`.
   */
  orderByFieldKey: v.optional(v.string()),
  /** Sentido del orden (por defecto ascendente; descendente no aplica a la posición). */
  orderDirection: v.optional(v.union(v.literal("asc"), v.literal("desc"))),
});

/** Topes de las reglas (F2). */
export const MAX_COLLECTION_VALIDATIONS = 30;
export const MAX_STATE_TRANSITIONS = 50;

/**
 * Regla de validación (F2): las condiciones deben cumplirse para guardar; si
 * no, se rechaza con `message`. Mismas condiciones que automatizaciones y
 * acciones de fila.
 */
export const collectionValidation = v.object({
  conditions: v.array(automationCondition),
  message: v.string(),
  /** Al crear, al editar o en ambos. */
  on: v.array(v.union(v.literal("create"), v.literal("update"))),
  /** Solo cuando el registro queda en uno de estos estados (máquina de estados). */
  states: v.optional(v.array(v.string())),
});

/** Máquina de estados sobre un campo de lista (F2). */
export const collectionStates = v.object({
  /** Campo de lista que guarda el estado. */
  fieldKey: v.string(),
  /** Estado con el que nace un registro. */
  initial: v.optional(v.string()),
  transitions: v.array(
    v.object({
      /** Estado de origen o "*" (cualquiera). */
      from: v.string(),
      to: v.string(),
      /** Texto del botón («Enviar», «Aceptar»). */
      label: v.string(),
      /** Roles que pueden hacerla (vacío = todos; los administradores siempre). */
      roleIds: v.optional(v.array(v.id("roles"))),
      /** Condiciones sobre el registro para permitirla. */
      conditions: v.optional(v.array(automationCondition)),
      /** Efecto (F3): código que corre tras el cambio de estado, en la misma transacción. */
      effect: v.optional(v.string()),
    }),
  ),
  /** Campos que no se editan en un estado ("*" = ninguno). */
  locked: v.optional(
    v.array(
      v.object({
        state: v.string(),
        fields: v.union(v.literal("*"), v.array(v.string())),
      }),
    ),
  ),
});

/**
 * Inmutabilidad de los registros (ver `lib/records/lock.ts`). Un registro
 * sellado solo admite cambiar de estado por una transición de la máquina
 * (p. ej. «Emitido» → «Anulado»); el resto de sus valores, sus líneas y el
 * borrado quedan bloqueados para todos, administradores incluidos.
 */
export const collectionSeal = v.object({
  /** «create»: cada registro nace sellado (bitácora). «states»: al entrar en uno de `states`. */
  on: v.union(v.literal("create"), v.literal("states")),
  states: v.optional(v.array(v.string())),
});

/**
 * Versionado de registros (como las versiones de Cotizaciones): «Nueva
 * versión» congela el registro con sus líneas en `collection_record_versions`
 * y el registro sigue como la versión siguiente. El registro es siempre la
 * versión vigente (la que ven la tabla, el kanban, los resúmenes y las
 * relaciones); las anteriores se consultan en solo lectura y cualquiera puede
 * volver a ser la vigente. Ver `versions.ts`.
 */
export const collectionVersioning = v.object({
  enabled: v.boolean(),
  /**
   * Campos de la cabecera que «Nueva versión → solo líneas» conserva además
   * de los fijos (ver `versionKeptKeys` en versioning_logic.ts).
   */
  keep: v.optional(v.array(v.string())),
  /** Campos que la versión nueva deja vacíos con cualquier opción (`versionResetKeys`). */
  reset: v.optional(v.array(v.string())),
});

/** Alcance de una colección de configuración: un registro por empresa o por sucursal. */
/** «Importar del sistema» (ver lib/schema/system_import.ts). */
export const collectionImportFrom = v.object({
  table: v.union(v.literal("clients"), v.literal("products"), v.literal("sellers")),
  fields: v.array(v.object({ from: v.string(), to: v.string(), values: v.optional(v.record(v.string(), v.string())) })),
  sourceField: v.optional(v.string()),
  match: v.optional(v.string()),
});

export const collectionConfigScope = v.union(v.literal("company"), v.literal("branch"));
export const collectionConfig = v.object({ scope: collectionConfigScope });

/** Formulario (F4): secciones, reglas por campo y permisos de edición por campo. */
export const collectionForm = v.object({
  sections: v.optional(
    v.array(
      v.object({
        title: v.string(),
        fieldKeys: v.array(v.string()),
        /** Texto de ayuda bajo el título. */
        description: v.optional(v.string()),
        /** Columnas de la rejilla de campos (1–4). Sin valor: la adaptable. */
        columns: v.optional(v.number()),
        /** Ancho de la sección en la página (para ponerlas lado a lado). */
        width: v.optional(
          v.union(v.literal("full"), v.literal("two_thirds"), v.literal("half"), v.literal("third")),
        ),
        /** Se puede plegar; `collapsed`: empieza cerrada. */
        collapsible: v.optional(v.boolean()),
        collapsed: v.optional(v.boolean()),
        /** Pestaña a la que pertenece (las secciones sin pestaña van arriba). */
        tab: v.optional(v.string()),
        /** Sin título visible en el formulario. */
        hideTitle: v.optional(v.boolean()),
      }),
    ),
  ),
  /** Columnas que ocupa cada campo dentro de su sección (1–4; ≥ columnas = todo el ancho). */
  fieldWidths: v.optional(v.array(v.object({ fieldKey: v.string(), span: v.number() }))),
  /**
   * Por tabla de líneas (`lines:<clave>`): agregados en una fila de pie,
   * campos del documento en el pie (se editan ahí), columnas ocultas, título
   * y filas de pie dentro de la tabla.
   */
  lines: v.optional(
    v.array(
      v.object({
        key: v.string(),
        aggregates: v.optional(
          v.array(
            v.object({
              fieldKey: v.string(),
              op: v.union(v.literal("sum"), v.literal("avg"), v.literal("min"), v.literal("max"), v.literal("count")),
            }),
          ),
        ),
        footerFields: v.optional(v.array(v.string())),
        hiddenColumns: v.optional(v.array(v.string())),
        /** Título de la tabla (en lugar del nombre de la hija) y sin contador. */
        title: v.optional(v.string()),
        hideCount: v.optional(v.boolean()),
        /** Filas del pie dentro de la tabla: por columna, un agregado o campos del documento. */
        rows: v.optional(
          v.array(
            v.object({
              label: v.optional(v.string()),
              cells: v.array(
                v.object({
                  column: v.string(),
                  op: v.optional(
                    v.union(v.literal("sum"), v.literal("avg"), v.literal("min"), v.literal("max"), v.literal("count")),
                  ),
                  fields: v.optional(v.array(v.string())),
                }),
              ),
            }),
          ),
        ),
      }),
    ),
  ),
  rules: v.optional(
    v.array(
      v.object({
        fieldKey: v.string(),
        visibleWhen: v.optional(v.array(automationCondition)),
        requiredWhen: v.optional(v.array(automationCondition)),
        on: v.optional(v.array(v.union(v.literal("create"), v.literal("update")))),
      }),
    ),
  ),
  fieldRoles: v.optional(v.array(v.object({ fieldKey: v.string(), roleIds: v.array(v.id("roles")) }))),
  /**
   * Cómo se abre el formulario del registro: «page» = ruta propia con el
   * aspecto del formulario de Cotizaciones; ausente = diálogo o panel
   * lateral, según la preferencia de cada usuario.
   */
  display: v.optional(v.literal("page")),
  /** Cerrar el formulario al guardar. Sin valor: el diálogo sí, la página no. */
  closeOnSave: v.optional(v.boolean()),
  /** Guardar solo los cambios al editar un registro existente (en página o
   *  diálogo; el panel lateral sigue con «Guardar»). */
  autoSave: v.optional(v.boolean()),
  /**
   * Tablas de líneas (`lines:<colección hija>`) que no se muestran en el
   * formulario. Sin valor, las que no están en ninguna sección van al final.
   */
  hiddenLines: v.optional(v.array(v.string())),
  /** Ocultar «Relacionados» (registros de otras colecciones que apuntan aquí). */
  hideRelated: v.optional(v.boolean()),
});

/** Un script de una acción predeterminada (antes de crear, editar o eliminar). */
export const beforeSaveScript = v.object({ code: v.string(), enabled: v.boolean() });

/** Scripts de las acciones predeterminadas, uno por acción. */
export const collectionBeforeSaveScripts = v.object({
  create: v.optional(beforeSaveScript),
  update: v.optional(beforeSaveScript),
  delete: v.optional(beforeSaveScript),
});

/**
 * Forma anterior: un solo script con sus eventos. Se acepta mientras corre la
 * migración `migrateBeforeSaveShape` (before_save.ts); todo se escribe ya con
 * la forma nueva y `beforeSaveScripts` normaliza al leer.
 */
export const legacyCollectionBeforeSave = v.object({
  code: v.string(),
  on: v.array(v.union(v.literal("create"), v.literal("update"))),
  enabled: v.boolean(),
});

export const collectionBeforeSave = v.union(collectionBeforeSaveScripts, legacyCollectionBeforeSave);

export const validatorCollection = v.object({
  name: v.string(),
  description: v.optional(v.string()),
  /** Icono de lucide (nombre del componente). */
  icon: v.optional(v.string()),
  /**
   * Key estable de la colección, separada del nombre visible. Es la que
   * usa el manifiesto de configuración global y los archivos portables
   * para referirse a una colección sin conocer su id interno. Se deriva
   * del nombre al crearla (slug) y no cambia al renombrar.
   *
   * Para registros existentes fue rellenada por
   * `migrations/migrate_collection_key.ts` en el rollout de F2. Misma
   * regla de slug que `convex/lib/schema/manifest.ts:collectionKey`.
   */
  key: v.string(),
  /** Key del campo que identifica cada registro (título en tabla/selectores). */
  displayFieldKey: v.optional(v.string()),
  /**
   * Grupo con nombre (p. ej. «Ventas»): junta colecciones relacionadas en el
   * lienzo de esquema. Ausente = sin grupo (el lienzo puede sugerir uno).
   */
  group: v.optional(v.string()),
  /**
   * Icono (lucide) del grupo en el menú. Se guarda en cada colección del
   * grupo (los grupos no tienen tabla propia); al entrar en un grupo, la
   * colección toma el de sus compañeras.
   */
  groupIcon: v.optional(v.string()),
  /**
   * Roles que pueden ver la colección. Vacío/ausente = todos los roles con
   * permiso del módulo `collection_records`.
   */
  allowedRoleIds: v.optional(v.array(v.id("roles"))),
  /**
   * Menú lateral: `admins` solo la ven ahí los administradores; `hidden`
   * nadie (líneas de documentos, catálogos de configuración). No cambia el
   * acceso: se abre desde Configuración → Colecciones, relaciones y líneas.
   * Ausente = todos los que tienen acceso.
   */
  menu: v.optional(v.union(v.literal("admins"), v.literal("hidden"))),
  /**
   * «Importar del sistema»: tabla del sistema de la que el botón de la
   * colección copia registros y a qué campos va cada dato (ver
   * lib/schema/system_import.ts y system_import.ts).
   */
  importFrom: v.optional(collectionImportFrom),
  /**
   * Roles que pueden crear / editar / borrar registros en ESTA colección
   * (además del permiso del módulo). Ausente o vacío = todos los que lo
   * tienen. Los administradores siempre pueden.
   */
  recordRoleIds: v.optional(
    v.object({
      create: v.optional(v.array(v.id("roles"))),
      update: v.optional(v.array(v.id("roles"))),
      delete: v.optional(v.array(v.id("roles"))),
      /** Descargar el PDF del registro. */
      pdf: v.optional(v.array(v.id("roles"))),
    }),
  ),
  /**
   * Colecciones hijas (líneas de un documento, F1 de
   * docs/analisis-modulos-complejos-con-colecciones.md). Cada hija es otra
   * colección cuyos registros apuntan a este con `parentFieldKey` (campo
   * relación a esta colección). El formulario del registro edita sus líneas
   * embebidas y `saveCollectionDocument` guarda todo en una mutation.
   * Una colección solo puede ser hija de un padre, y una hija no tiene hijas.
   */
  children: v.optional(v.array(collectionChild)),
  /** Reglas que bloquean el guardado (F2). */
  validations: v.optional(v.array(collectionValidation)),
  /** Máquina de estados (F2). */
  states: v.optional(collectionStates),
  /** Sello de inmutabilidad de los registros. */
  seal: v.optional(collectionSeal),
  /** Versionado de registros (ver `collectionVersioning`). */
  versioning: v.optional(collectionVersioning),
  /** Scripts que corren antes de crear, editar o eliminar un registro. */
  beforeSave: v.optional(collectionBeforeSave),
  /** Formulario (F4). */
  form: v.optional(collectionForm),
  /**
   * Cuándo terminó de indexarse en `collection_relations` (aristas de sus
   * campos relación). Ausente: las lecturas inversas sobre esta colección
   * siguen recorriendo sus registros. Ver `relations.ts`.
   */
  relationsIndexedAt: v.optional(v.number()),
  /**
   * `"private"`: colección personal. Solo la ven su dueño (`ownerId`) y las
   * personas con quienes la comparte (`internalControl.canView` ve,
   * `canEdit` edita). Ni los admins la ven. Ausente = de la empresa.
   */
  visibility: v.optional(v.union(v.literal("company"), v.literal("private"))),
  /** Dueño de una colección privada (quien la creó, o el admin que la heredó). */
  ownerId: v.optional(v.id("users")),
  /**
   * Colección de configuración (docs/analisis-colecciones-de-configuracion.md):
   * un registro por empresa (`company`) o por sucursal (`branch`), que se crea
   * con los valores por defecto al primer uso, no se borra («Restablecer») y
   * se edita desde Configuración → Ajustes de colecciones. Ver `config.ts`.
   */
  config: v.optional(collectionConfig),
  /**
   * «Disponible sin conexión» (docs/diseno-libreria-local-first.md): los
   * dispositivos replican sus registros y los editan sin red; el servidor
   * decide al sincronizar. `window`: solo los registros cuya fecha cae en la
   * ventana. `readOnly`: se replica pero no se edita sin red. `reviewFields`:
   * campos que van a revisión ante un conflicto (además del campo de estado).
   */
  offline: v.optional(
    v.object({
      enabled: v.boolean(),
      maxRows: v.optional(v.number()),
      window: v.optional(v.object({ field: v.string(), pastDays: v.number(), futureDays: v.number() })),
      readOnly: v.optional(v.boolean()),
      reviewFields: v.optional(v.array(v.string())),
      /** Colección de líneas: disponible porque lo está su documento (no por sí misma). */
      lineOf: v.optional(v.id("collections")),
    }),
  ),
  /**
   * Versión de los campos de la colección: sube cuando se agrega, borra o
   * cambia de tipo un campo. Las operaciones offline la llevan; una distinta
   * se traduce o se rechaza de forma visible.
   */
  fieldsVersion: v.optional(v.number()),
  /**
   * Ranuras indexables (`ix_s*`, `ix_n*` en `collection_records`): qué campo
   * filtrable ocupa cada una. `since` es la versión en que se asignó; una
   * ranura se usa para leer cuando `since <= readyVersion` (su backfill
   * terminó). Ver `slots.ts`.
   */
  indexedSlots: v.optional(
    v.object({
      version: v.number(),
      readyVersion: v.number(),
      slots: v.array(
        v.object({
          slot: v.string(),
          fieldKey: v.string(),
          fieldType: v.string(),
          since: v.number(),
        }),
      ),
    }),
  ),
  order: v.number(),
  internalControl,
});

export const collections = defineTableWithInternalControl(
  validatorCollection,
)
  .index("by_company_and_order", ["internalControl.companyId", "order"])
  /**
   * Único por empresa: el manifiesto usa `key` como identificador portable.
   * Si dos colecciones tuvieran la misma key, el archivo JSON sería
   * ambiguo. La validación de unicidad la hace el backfill y `createCollection`;
   * el índice acelera la búsqueda al resolver relaciones del manifiesto.
   */
  .index("by_company_and_key", ["internalControl.companyId", "key"]);

/** Actor y origen de una escritura de registro (ver `record_history`). */
export const writeOrigin = v.object({
  kind: v.union(v.literal("user"), v.literal("agent"), v.literal("script"), v.literal("automation"), v.literal("system"), v.literal("site")),
  at: v.number(),
  /** Una por escritura: distingue la que puso el origen de otra posterior en el mismo milisegundo. */
  writeId: v.optional(v.string()),
  tokenId: v.optional(v.string()),
  sourceId: v.optional(v.string()),
  opId: v.optional(v.string()),
  offline: v.optional(v.boolean()),
  deviceId: v.optional(v.string()),
  madeAt: v.optional(v.number()),
});

export const validatorCollectionRecord = v.object({
  collectionId: v.id("collections"),
  /**
   * Valores por key de campo (validados contra las definiciones al guardar).
   */
  values: customFieldValues,
  /**
   * Identificador público del registro: UUID v4 generado en
   * `createCollectionRecord` con `crypto.randomUUID()`. El shape
   * pasó de `v.optional(v.string())` (Fase A, donde aún había
   * registros viejos sin uuid) a `v.string()` después del
   * backfill (Fase B → migración `backfillCollectionRecordsUuid`
   * en `convex/migrations/migrate_collection_records_uuid.ts`).
   * Revertir a `v.optional` solo si se hace un nuevo rollout a
   * una base con registros pre-creados.
   *
   * Se expone como `record._uuid` en funciones JS de campos
   * `function` y en automatizaciones, además de como columna
   * virtual ocultable en las tablas.
   */
  uuid: v.string(),
  /** Texto de los valores, para la búsqueda de la tabla. */
  searchText: v.string(),
  /**
   * Registro padre cuando la colección es hija de otra (`children`): copia
   * de `values[parentFieldKey]`, para leer las líneas de un documento por
   * índice (los valores viven en un objeto sin índice).
   */
  parentId: v.optional(v.id("collection_records")),
  /**
   * Ranuras indexables: copia de los campos filtrables de la colección
   * (`collections.indexedSlots`), para filtrar y ordenar por índice. Las
   * mantiene un trigger (lib/records/record_slots.ts). Texto en `ix_s*`
   * (normalizado en los campos de texto libre), números en `ix_n*`
   * (casillas como 1/0, fechas en milisegundos).
   */
  ix_s1: v.optional(v.string()),
  ix_s2: v.optional(v.string()),
  ix_s3: v.optional(v.string()),
  ix_s4: v.optional(v.string()),
  ix_n1: v.optional(v.number()),
  ix_n2: v.optional(v.number()),
  ix_n3: v.optional(v.number()),
  ix_n4: v.optional(v.number()),
  /**
   * Quién y desde dónde escribió por última vez (historial por registro). Lo
   * estampan los …Core; `at` distingue la escritura que lo puso de otras
   * posteriores que no lo actualizan (p. ej. un rollup programado).
   */
  lastWriteOrigin: v.optional(writeOrigin),
  /**
   * Número de la versión vigente cuando la colección tiene versionado
   * (ausente = 1). Las anteriores viven en `collection_record_versions`.
   */
  version: v.optional(v.number()),
  internalControl,
});

export const collection_records = defineTableWithInternalControl(
  validatorCollectionRecord,
)
  .index("by_company_and_collection", ["internalControl.companyId", "collectionId"])
  // Registros creados sin conexión: el uuid lo genera el cliente.
  .index("by_company_and_uuid", ["internalControl.companyId", "uuid"])
  // Registros de una coleccion acotados por sucursal (masterDataScope "branch").
  .index("by_company_and_collection_and_branch", [
    "internalControl.companyId",
    "collectionId",
    "internalControl.branchId",
  ])
  // Líneas de un documento (colecciones hijas).
  .index("by_company_and_collection_and_parent", [
    "internalControl.companyId",
    "collectionId",
    "parentId",
  ])
  // Ranuras indexables (ver `ix_*`).
  .index("by_collection_s1", ["internalControl.companyId", "collectionId", "ix_s1"])
  .index("by_collection_s2", ["internalControl.companyId", "collectionId", "ix_s2"])
  .index("by_collection_s3", ["internalControl.companyId", "collectionId", "ix_s3"])
  .index("by_collection_s4", ["internalControl.companyId", "collectionId", "ix_s4"])
  .index("by_collection_n1", ["internalControl.companyId", "collectionId", "ix_n1"])
  .index("by_collection_n2", ["internalControl.companyId", "collectionId", "ix_n2"])
  .index("by_collection_n3", ["internalControl.companyId", "collectionId", "ix_n3"])
  .index("by_collection_n4", ["internalControl.companyId", "collectionId", "ix_n4"])
  // Búsqueda global (system/global_search.ts) sobre el texto de los valores.
  .searchIndex("search_collection_records", {
    searchField: "searchText",
    filterFields: ["internalControl.companyId", "collectionId"],
  });

// ── Versiones anteriores de un registro (versionado) ───────────────────
// Copia congelada de un registro y sus líneas al crear una versión nueva (o al
// marcar otra como vigente). Nunca se edita: se lee en solo lectura y, si se
// marca como vigente, sus valores vuelven al registro y la copia se borra.
// Un número de versión está en una sola parte: aquí o en el registro.
export const collection_record_versions = defineTable({
  companyId: v.id("companies"),
  collectionId: v.id("collections"),
  recordId: v.id("collection_records"),
  version: v.number(),
  /** Valores del registro en esa versión. */
  values: customFieldValues,
  /** Líneas de cada colección hija, en orden (sin el campo relación al padre). */
  lines: v.array(v.object({ collectionId: v.id("collections"), values: customFieldValues })),
  /** Cuándo y quién la congeló. */
  frozenAt: v.number(),
  frozenBy: v.optional(v.id("users")),
})
  .index("by_record_and_version", ["recordId", "version"])
  .index("by_collection", ["collectionId"]);

// ── Contadores de autonumeración (F3) ─────────────────────────────────────
// Uno por colección, campo y período ("" sin reinicio, "2026" anual,
// "2026-10" mensual). Se incrementa en la misma mutation que guarda el
// registro: Convex serializa las transacciones, así que no hay saltos ni
// números repetidos.
export const validatorCollectionCounter = v.object({
  collectionId: v.id("collections"),
  fieldKey: v.string(),
  period: v.string(),
  value: v.number(),
  internalControl,
});

export const collection_counters = defineTableWithInternalControl(validatorCollectionCounter).index(
  "by_company_and_collection_and_field",
  ["internalControl.companyId", "collectionId", "fieldKey", "period"],
);

// ── Aristas de relaciones entre registros ──────────────────────────────
// Una fila por cada valor de un campo `relation` que apunta a otro registro de
// colección (docs/analisis-colecciones-relaciones-eficiencia.md, §3.2). Las
// mantiene un trigger de `collection_records` (lib/records/relation_edges.ts)
// en la misma transacción que escribe el registro. Sirven para leer al revés
// ("los mantenimientos de este vehículo") sin recorrer la colección de origen,
// y para la integridad al borrar.
export const collection_relations = defineTable({
  companyId: v.id("companies"),
  /** Registro que tiene el campo relación. */
  fromId: v.id("collection_records"),
  fromCollectionId: v.id("collections"),
  /** Key del campo relación en la colección de origen. */
  fieldKey: v.string(),
  /** Registro apuntado. */
  toId: v.id("collection_records"),
  toCollectionId: v.id("collections"),
})
  .index("by_to", ["toId", "fromCollectionId", "fieldKey"])
  .index("by_from", ["fromId", "fieldKey"]);

// ── Triggers sobre records (Apps Script-style) ─────────────────────────
// Codigo del admin que corre cuando un record se crea, actualiza o borra.
// Evalua contra el sandbox de function fields (mismas defenses).

export const validatorCollectionFunctionTrigger = v.object({
  /** Coleccion a la que pertenece el trigger. */
  collectionId: v.id("collections"),
  /** Evento que dispara el trigger. */
  event: v.union(
    v.literal("onCreate"),
    v.literal("onUpdate"),
    v.literal("onDelete"),
  ),
  /** Etiqueta legible del admin (para listar/editar). */
  name: v.string(),
  /** Codigo JS que el sandbox evalua cuando se dispara. Recibe
   *  `{record, previousRecord, changes, user, event, timestamp}` como
   *  primer argumento. Max 5000 chars (igual que function fields). */
  code: v.string(),
  /** Si false, el trigger se ignora sin borrarlo (pausa). */
  enabled: v.boolean(),
  internalControl,
});

export const collection_function_triggers = defineTableWithInternalControl(
  validatorCollectionFunctionTrigger,
)
  .index("by_collection_event", ["collectionId", "event"])
  .index("by_company_and_event", [
    "internalControl.companyId",
    "event",
  ]);
